package macha.sh.kotlin.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import macha.sh.kotlin.components.sections.NaviLayout
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Main
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLScriptElement

data class PageLayoutData(
    val title: String,
    val description: String,
    val path: String,
    val indexable: Boolean = true,
    val markdownPath: String? = null,
)
val SiteContentMaxWidth = 75.cssRem
val PageContentStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(leftRight = 1.5.cssRem, top = 2.cssRem) }
    Breakpoint.MD { Modifier.maxWidth(SiteContentMaxWidth).padding(top = 1.cssRem) }
}
private const val SITE_ORIGIN = "https://macha.sh"

private fun upsertMeta(selector: String, attribute: String, value: String, content: String) {
    val head = document.head ?: return
    val existing = head.querySelector(selector) as? HTMLMetaElement
    val meta = existing ?: (document.createElement("meta") as HTMLMetaElement).also(head::appendChild)
    meta.setAttribute(attribute, value)
    meta.content = content
}

private fun upsertLink(selector: String, rel: String, href: String, type: String? = null) {
    val head = document.head ?: return
    val existing = head.querySelector(selector) as? HTMLLinkElement
    val link = existing ?: (document.createElement("link") as HTMLLinkElement).also(head::appendChild)
    link.rel = rel
    link.href = href
    type?.let { link.type = it }
}

private fun applyPageMetadata(data: PageLayoutData) {
    val canonicalUrl = SITE_ORIGIN + data.path
    val pageTitle = if (data.path == "/") {
        "Macha — Generalist Developer & DevOps Engineer"
    } else {
        "${data.title} | Macha"
    }

    document.title = pageTitle
    upsertMeta("meta[name='description']", "name", "description", data.description)
    upsertMeta("meta[name='robots']", "name", "robots", if (data.indexable) "index, follow" else "noindex, follow")
    upsertMeta("meta[property='og:title']", "property", "og:title", pageTitle)
    upsertMeta("meta[property='og:description']", "property", "og:description", data.description)
    upsertMeta("meta[property='og:type']", "property", "og:type", "website")
    upsertMeta("meta[property='og:url']", "property", "og:url", canonicalUrl)
    upsertMeta("meta[property='og:image']", "property", "og:image", "$SITE_ORIGIN/img-content/Avatar.png")
    upsertMeta("meta[name='twitter:card']", "name", "twitter:card", "summary")
    upsertMeta("meta[name='twitter:title']", "name", "twitter:title", pageTitle)
    upsertMeta("meta[name='twitter:description']", "name", "twitter:description", data.description)
    upsertLink("link[rel='canonical']", "canonical", canonicalUrl)

    document.head?.querySelector("link[data-page-markdown='true']")?.remove()
    data.markdownPath?.let { markdownPath ->
        upsertLink("link[data-page-markdown='true']", "alternate", SITE_ORIGIN + markdownPath, "text/markdown")
        document.head?.querySelector("link[rel='alternate'][type='text/markdown']")
            ?.setAttribute("data-page-markdown", "true")
    }
    upsertLink("link[rel='describedby']", "describedby", "$SITE_ORIGIN/llms.txt", "text/markdown")

    val head = document.head ?: return
    val script = (head.querySelector("#site-identity-json-ld") as? HTMLScriptElement)
        ?: (document.createElement("script") as HTMLScriptElement).also {
            it.id = "site-identity-json-ld"
            it.type = "application/ld+json"
            head.appendChild(it)
        }
    script.textContent = """
        {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": "$SITE_ORIGIN/#website",
              "url": "$SITE_ORIGIN/",
              "name": "Macha"
            },
            {
              "@type": "Person",
              "@id": "$SITE_ORIGIN/#macha",
              "name": "Macha",
              "url": "$SITE_ORIGIN/",
              "image": "$SITE_ORIGIN/img-content/Avatar.png",
              "jobTitle": ["Generalist Developer", "DevOps Engineer"]
            }
          ]
        }
    """.trimIndent()
}

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    LaunchedEffect(data) { applyPageMetadata(data) }
    Box(Modifier.fillMaxWidth().minHeight(60.vh), contentAlignment = Alignment.Center) {
        Column(Modifier.fillMaxSize(), horizontalAlignment = Alignment.CenterHorizontally) {
            Header(attrs = Modifier.fillMaxWidth().toAttrs()) {
                NaviLayout.Content()
            }
            Main(attrs = PageContentStyle.toAttrs()) {
                Column(Modifier.fillMaxWidth()) {
                    content()
                }
            }
        }
    }
}
// Isolate the content, because otherwise the absolute-positioned SVG above will render on top of it.
// This is confusing but how browsers work. Read up on stacking contexts for more info.
// https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
// Some people might have used z-index instead, but best practice is to avoid that if possible, because
// as a site gets complex, Z-fighting can be a huge pain to track down.